'use client';
import { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import SIGN_IN_FORM_CONFIG from '@/app/(auth)/sign-in/form-config';
import Button from '@/components/ui/buttons/button';
import IconButton from '@/components/ui/buttons/icon-button';
import TextField from '@/components/ui/input-fields/text-input-field';
import BodyText from '@/components/ui/typography/body-text';
import { FIREBASE_AUTH_ERROR_MESSAGES, FIREBASE_AUTH_SUCCESS_MESSAGES } from '@/constants/firebase';
import AUTH_PATHS from '@/constants/paths/auth.path';
import { errorCatcher } from '@/lib/error-catcher';
import { firebaseAuth } from '@/lib/firebase/firebase-client';
import { setFirebaseAdminAuthSession } from '@/server-actions/auth.action';
import { FireBaseAuthSignInPayload } from '@/types/auth.type';

export default function SignInForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isResetPasswordSuccess = searchParams.get('oobCode') || '';
  const callbackUrl = searchParams.get('callbackUrl') || '';
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isFirebaseAuthSignInLoading, setIsFirebaseAuthSignInLoading] = useState(false);
  const {
    control,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<FireBaseAuthSignInPayload>({
    mode: 'onChange',
    defaultValues: SIGN_IN_FORM_CONFIG.defaultValues,
    resolver: zodResolver(SIGN_IN_FORM_CONFIG.schema),
  });

  const handleGoSignUpClick = () => {
    router.push(AUTH_PATHS.signUp.pathname);
  };
  const handleGoToForgotPasswordClick = () => {
    router.push(AUTH_PATHS.forgotPassword.pathname);
  };

  const handleSignInSubmit: SubmitHandler<FireBaseAuthSignInPayload> = async (data) => {
    try {
      setIsFirebaseAuthSignInLoading(true);
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        data.companyEmail,
        data.password
      );
      if (!userCredential.user.emailVerified) {
        setError('companyEmail', {
          message: FIREBASE_AUTH_ERROR_MESSAGES['auth/user-not-verified'],
        });
        return;
      }
      // * 取得 Firebase ID token
      const firebaseAuthIdToken = await userCredential.user.getIdToken();
      // * Set session
      await setFirebaseAdminAuthSession({ firebaseAuthIdToken });
      // * 檢查是否有 callback Url
      router.push(callbackUrl || '/');
    } catch (error: unknown) {
      const errorMessage = errorCatcher(error);
      if (errorMessage) {
        setError('companyEmail', { message: '' });
        setError('password', { message: errorMessage });
      }
    } finally {
      setIsFirebaseAuthSignInLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(handleSignInSubmit)}
      className="w-full flex flex-col gap-y-[0.625rem]">
      {/* Reset Password Success Message (if has) */}
      {isResetPasswordSuccess ? (
        <BodyText className="text-achromatic-light mb-2 text-center">
          {FIREBASE_AUTH_SUCCESS_MESSAGES.resetPasswordSuccess}
        </BodyText>
      ) : null}
      {/* Email */}
      <Controller
        control={control}
        name={SIGN_IN_FORM_CONFIG.baseAttributes.companyEmail.name}
        render={({ field: { name, value, onChange }, fieldState: { invalid } }) => (
          <TextField
            required={SIGN_IN_FORM_CONFIG.baseAttributes.companyEmail.required}
            label={SIGN_IN_FORM_CONFIG.baseAttributes.companyEmail.label}
            placeholder={SIGN_IN_FORM_CONFIG.baseAttributes.companyEmail.placeholder}
            inputName={name}
            inputValue={value}
            onInputChange={onChange}
            isError={invalid}
            error={errors.companyEmail?.message}
          />
        )}
      />
      {/* Password */}
      <Controller
        control={control}
        name={SIGN_IN_FORM_CONFIG.baseAttributes.password.name}
        render={({ field: { name, value, onChange }, fieldState: { invalid } }) => (
          <TextField
            required={SIGN_IN_FORM_CONFIG.baseAttributes.password.required}
            label={SIGN_IN_FORM_CONFIG.baseAttributes.password.label}
            placeholder={SIGN_IN_FORM_CONFIG.baseAttributes.password.placeholder}
            inputType={isShowPassword ? 'text' : 'password'}
            inputName={name}
            inputValue={value}
            onInputChange={onChange}
            isError={invalid}
            error={errors.password?.message}
            endElement={
              isShowPassword ? (
                <IconButton size="xs" icon="visibility" onClick={() => setIsShowPassword(false)} />
              ) : (
                <IconButton
                  size="xs"
                  icon="visibility_off"
                  onClick={() => setIsShowPassword(true)}
                />
              )
            }
          />
        )}
      />
      {/* Forgot Password Button */}
      <Button
        variant="borderless"
        size="xs"
        className="ml-auto"
        onClick={handleGoToForgotPasswordClick}>
        {AUTH_PATHS.forgotPassword.name}
      </Button>
      {/* Submit Button */}
      <Button
        type="submit"
        isLoading={isFirebaseAuthSignInLoading}
        disabled={isFirebaseAuthSignInLoading}
        className="w-full my-[0.625rem]">
        登入
      </Button>
      {/* Go To Sign Up Button */}
      <Button variant="borderless" className="w-full" onClick={handleGoSignUpClick}>
        沒有帳戶？註冊
      </Button>
    </form>
  );
}
